import { useCallback, useState } from 'react';

export interface InvalidDecimalKeys {
    hasInvalidKeys: boolean;
    onValidityChange: (key: string, isValid: boolean) => void;
    /** Passed to the fields: a change puts them back on the stored values */
    resetKey: number;
    reset: () => void;
}

/**
 * Owner side of the decimal fields outside Formik: the action that consumes the values stays blocked
 * while any field reports invalid text. A valid report clears only its own key.
 */
export const useInvalidDecimalKeys = (): InvalidDecimalKeys => {
    const [invalidKeys, setInvalidKeys] = useState<ReadonlySet<string>>(() => new Set());
    const [resetKey, setResetKey] = useState(0);

    const onValidityChange = useCallback((key: string, isValid: boolean) => {
        setInvalidKeys(current => {
            if (isValid !== current.has(key)) return current;
            const next = new Set(current);
            if (isValid) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const reset = useCallback(() => {
        setInvalidKeys(new Set());
        setResetKey(key => key + 1);
    }, []);

    return { hasInvalidKeys: invalidKeys.size > 0, onValidityChange, resetKey, reset };
};
